
import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./usermarkup";
import { usePage } from "../hooks/usePage";

import {
  getOfficers,
  createOfficer,
  updateOfficer,
  deleteOfficer,
  clearUserError,
} from "../store/slices/user";

import { getSubDivisions } from "../store/slices/subdivision";
import { getCircles } from "../store/slices/circle";
import { getStations } from "../store/slices/station";
import { getBooths } from "../store/slices/booth";

const PageMarkup = memo(Markup);

// Must match the backend Mongoose designation enum
const DESIGNATIONS = [
  "Assistant Sub Inspector",
  "Police Constable",
  "Head Constable",
  "Sub Inspector",
  "Women Police Constable",
  "Circle Inspector",
  "Home Guard",
  "Deputy Superintedent of Police",
  "Additional  superintendent of Police",
];

const DUTY_WINGS = [
  "POLLING_STATION",
  "ROUTE_MOBILE",
  "CHECK_POST",
  "STRIKING_FORCE",
  "FLYING_SQUAD",
  "STATIC_SURVEILLANCE",
  "VIDEO_SURVEILLANCE",
  "COORDINATION_TEAM",
  "GAS_PARTY",
  "OTHER",
];

const initialForm = {
  username: "",
  displayName: "",
  phone: "",
  password: "",
  badgeNo: "",
  designation: "Police Constable",
  designationName: "",
  dutyWing: "POLLING_STATION",
  subDivisionId: "",
  circleId: "",
  stationId: "",
  sectorId: "",
  boothId: "",
  specialDutyId: "",
  superiorOfficerId: "",
  vehicleNo: "",
  shift: "",
  active: true,
};

const getId = (value) => {
  if (!value) return "";
  return typeof value === "object" ? value._id || "" : value;
};

const getString = (value) => String(value ?? "");

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.message || error?.payload || fallback;
};

const normalizeOfficer = (officer) => ({
  ...initialForm,
  ...officer,
  username: getString(officer?.username),
  displayName: getString(officer?.displayName),
  phone: getString(officer?.phone),
  password: "",
  badgeNo: getString(officer?.badgeNo),
  designation: getString(officer?.designation) || initialForm.designation,
  designationName: getString(officer?.designationName),
  dutyWing: getString(officer?.dutyWing) || initialForm.dutyWing,
  subDivisionId: getId(officer?.subDivisionId),
  circleId: getId(officer?.circleId),
  stationId: getId(officer?.stationId),
  sectorId: getId(officer?.sectorId),
  boothId: getId(officer?.boothId),
  specialDutyId: getId(officer?.specialDutyId),
  superiorOfficerId: getId(officer?.superiorOfficerId),
  vehicleNo: getString(officer?.vehicleNo),
  shift: getString(officer?.shift),
  active: officer?.active ?? true,
});

export default function User() {
  usePage({
    title: "Officer Management - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  const {
    officers = [],
    loading,
    createLoading,
    updateLoading,
    deleteLoading,
    error,
    pagination,
  } = useSelector((state) => state.user);

  const { subdivisions = [] } = useSelector(
    (state) => state.subdivision
  );

  const { circles = [] } = useSelector(
    (state) => state.circle
  );

  const { stations = [] } = useSelector(
    (state) => state.station
  );

  const { booths = [] } = useSelector(
    (state) => state.booth
  );

  const [search, setSearch] = useState("");
  const [designationFilter, setDesignationFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("grid");

  const [showModal, setShowModal] = useState(false);
  const [editingOfficerId, setEditingOfficerId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");

  const isSaving = createLoading || updateLoading;

  // --------------------------------------------------
  // LOAD DATA
  // --------------------------------------------------

  const loadData = () => {
    dispatch(getOfficers({ page: 1, limit: 1000 }));
    dispatch(getSubDivisions({ page: 1, limit: 1000 }));
    dispatch(getCircles({ page: 1, limit: 1000 }));
    dispatch(getStations({ page: 1, limit: 1000 }));
    dispatch(getBooths({ page: 1, limit: 1000 }));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // --------------------------------------------------
  // FILTER OFFICERS
  // --------------------------------------------------

  const filteredOfficers = useMemo(() => {
    return officers.filter((officer) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        [
          officer.displayName,
          officer.username,
          officer.officerId,
          officer.badgeNo,
          officer.phone,
          officer.designation,
          officer.designationName,
        ].some((value) =>
          String(value || "").toLowerCase().includes(searchText)
        );

      const matchesDesignation =
        designationFilter === "ALL" ||
        officer.designation === designationFilter;

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && officer.active === true) ||
        (statusFilter === "INACTIVE" && officer.active === false);

      return (
        matchesSearch &&
        matchesDesignation &&
        matchesStatus
      );
    });
  }, [officers, search, designationFilter, statusFilter]);

  // --------------------------------------------------
  // STATISTICS
  // --------------------------------------------------

  const statistics = useMemo(
    () => ({
      total: officers.length,
      active: officers.filter((item) => item.active === true).length,
      inactive: officers.filter((item) => item.active === false).length,
      online: officers.filter((item) => item.isOnline === true).length,
    }),
    [officers]
  );

  // --------------------------------------------------
  // MODAL
  // --------------------------------------------------

  const openAddModal = () => {
    dispatch(clearUserError());
    setEditingOfficerId(null);
    setForm({ ...initialForm });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (officer) => {
    dispatch(clearUserError());

    setEditingOfficerId(officer._id);
    setForm(normalizeOfficer(officer));
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSaving) return;

    setShowModal(false);
    setEditingOfficerId(null);
    setForm({ ...initialForm });
    setFormError("");
  };

  // --------------------------------------------------
  // FORM HANDLERS
  // --------------------------------------------------

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubdivisionChange = (subDivisionId) => {
    setForm((previous) => ({
      ...previous,
      subDivisionId,
      circleId: "",
      stationId: "",
    }));
  };

  const handleCircleChange = (circleId) => {
    setForm((previous) => ({
      ...previous,
      circleId,
      stationId: "",
    }));
  };

  const handleDesignationChange = (designation) => {
    setForm((previous) => ({
      ...previous,
      designation,
      subDivisionId: "",
      circleId: "",
      stationId: "",
    }));
  };

  // --------------------------------------------------
  // CREATE / UPDATE
  // --------------------------------------------------

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setFormError("");

    const username = getString(form.username).trim();
    const displayName = getString(form.displayName).trim();
    const phone = getString(form.phone).trim();
    const password = getString(form.password);

    if (
      !username ||
      !displayName ||
      !phone ||
      !form.designation
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    if (!editingOfficerId && !password.trim()) {
      setFormError("Password is required when creating an officer.");
      return;
    }

    // Validate designation against backend-compatible values
    if (!DESIGNATIONS.includes(form.designation)) {
      setFormError("Please select a valid officer designation.");
      return;
    }

    const payload = {
      username: username.toLowerCase(),
      displayName,
      phone,
      badgeNo: getString(form.badgeNo).trim() || null,
      designation: form.designation,
      designationName: getString(form.designationName).trim() || null,
      dutyWing: form.dutyWing,

      subDivisionId: form.subDivisionId || null,
      circleId: form.circleId || null,
      stationId: form.stationId || null,
      sectorId: getString(form.sectorId).trim() || null,
      boothId: getString(form.boothId).trim() || null,
      specialDutyId: getString(form.specialDutyId).trim() || null,
      superiorOfficerId: form.superiorOfficerId || null,

      vehicleNo: getString(form.vehicleNo).trim() || null,
      shift: getString(form.shift).trim() || null,
      active: form.active,
    };

    if (password.trim()) {
      payload.password = password;
    }

    try {
      if (editingOfficerId) {
        await dispatch(
          updateOfficer({
            id: editingOfficerId,
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createOfficer(payload)).unwrap();
      }

      setShowModal(false);
      setEditingOfficerId(null);
      setForm({ ...initialForm });
      setFormError("");

      dispatch(getOfficers({ page: 1, limit: 1000 }));
    } catch (err) {
      setFormError(
        getErrorMessage(err, "Failed to save officer.")
      );
    }
  };

  // --------------------------------------------------
  // DELETE
  // --------------------------------------------------

  const handleDelete = async (officer) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${officer.displayName || officer.username
      }?`
    );

    if (!confirmed) return;

    try {
      await dispatch(deleteOfficer(officer._id)).unwrap();
      dispatch(getOfficers({ page: 1, limit: 1000 }));
    } catch (err) {
      window.alert(
        getErrorMessage(err, "Failed to delete officer.")
      );
    }
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <PageMarkup
      officers={filteredOfficers}
      totalOfficers={officers.length}
      pagination={pagination}
      statistics={statistics}
      loading={loading}
      createLoading={createLoading}
      updateLoading={updateLoading}
      deleteLoading={deleteLoading}
      error={error}
      search={search}
      setSearch={setSearch}
      designationFilter={designationFilter}
      setDesignationFilter={setDesignationFilter}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      viewMode={viewMode}
      setViewMode={setViewMode}
      showModal={showModal}
      editingOfficerId={editingOfficerId}
      form={form}
      formError={formError}
      subdivisions={subdivisions}
      circles={circles}
      stations={stations}
      officersList={officers}
      designations={DESIGNATIONS}
      dutyWings={DUTY_WINGS}
      onAddOfficer={openAddModal}
      onEditOfficer={openEditModal}
      onDeleteOfficer={handleDelete}
      onCloseModal={closeModal}
      onFormChange={handleFormChange}
      onSubdivisionChange={handleSubdivisionChange}
      onCircleChange={handleCircleChange}
      onDesignationChange={handleDesignationChange}
      onSubmit={handleSubmit}
      booths={booths}
    />
  );
}
